import React from "react";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Scale,
  ShoppingBag,
  Cpu,
  Building2,
  Megaphone,
  Landmark,
  Factory,
  ArrowUpRight,
} from "lucide-react";

const domains = [
  {
    id: "education",
    title: "Education",
    tagline: "Academies, coaching & learning",
    Icon: GraduationCap,
    image:
      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=900&q=70",
    desc:
      "Setting up coaching institutes, advising on curriculum, and building learning platforms that actually retain students — from first class to board results.",
    points: ["Institute setup & licensing", "Academic advisory", "Online learning platforms"],
  },
  {
    id: "tax",
    title: "Tax & Legal",
    tagline: "Compliance without the chaos",
    Icon: Scale,
    image:
      "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=900&q=70",
    desc:
      "Returns, registrations, notices, contracts — day-to-day work done right the first time, by people who understand small and mid-sized businesses.",
    points: ["GST / Income Tax", "Company & LLP compliance", "Contracts & notices"],
  },
  {
    id: "ecom",
    title: "E-commerce",
    tagline: "Online stores that actually sell",
    Icon: ShoppingBag,
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=900&q=70",
    desc:
      "Storefronts, marketplaces and logistics partners. From launch day to reliable Monday-morning sales — with numbers you can trust.",
    points: ["Shopify / WooCommerce", "Marketplace listing", "Logistics & payments"],
  },
  {
    id: "it",
    title: "IT Solutions",
    tagline: "Tech built for small teams",
    Icon: Cpu,
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=70",
    desc:
      "Websites, apps, CRMs and automations — built by people who understand what a small team can actually run, not what sounds impressive on a slide.",
    points: ["Websites & web apps", "CRM & automations", "Cloud & DevOps"],
  },
  {
    id: "realestate",
    title: "Real Estate",
    tagline: "Sourcing, advisory, transactions",
    Icon: Building2,
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=70",
    desc:
      "Properties, projects and investment advisory. Honest due-diligence and transactions you can actually trust — no layered brokers, no shortcuts.",
    points: ["Residential & commercial", "Due-diligence", "Project advisory"],
  },
  {
    id: "marketing",
    title: "Marketing",
    tagline: "Budgets spent where they convert",
    Icon: Megaphone,
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=900&q=70",
    desc:
      "Local campaigns, performance ads and brand work. We measure what matters and cut what doesn't — spend that actually brings in business.",
    points: ["Meta & Google Ads", "Brand & content", "Local & offline"],
  },
  {
    id: "finance",
    title: "Finance",
    tagline: "The numbers behind decisions",
    Icon: Landmark,
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=70",
    desc:
      "Bookkeeping, planning and funding advisory. Month-end closings on time and forecasts you can defend.",
    points: ["Accounting & MIS", "Financial planning", "Funding & investor prep"],
  },
  {
    id: "manufacturing",
    title: "Manufacturing",
    tagline: "Industrial & operations advisory",
    Icon: Factory,
    image:
      "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=900&q=70",
    desc:
      "Factory setup, procurement planning and operational improvements for small-to-mid industrial units. Practical, on-the-ground support.",
    points: ["Unit setup & licensing", "Procurement & sourcing", "Ops & quality"],
  },
];

const DomainCard = ({ d, index }) => {
  const { Icon } = d;
  const [flipped, setFlipped] = React.useState(false);

  const handleAsk = (e) => {
    e.stopPropagation();
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`domain-card-${d.id}`}
      onClick={() => {
        if (isTouch) setFlipped((f) => !f);
      }}
      className={`flip-card h-[380px] sm:h-[400px] md:h-[420px] ${
        flipped ? "is-flipped" : ""
      }`}
      tabIndex={0}
    >
      <div className="flip-inner h-full">
        {/* FRONT — photo-led card */}
        <div className="flip-face rounded-3xl overflow-hidden glass-strong flex flex-col">
          <div className="relative h-44 overflow-hidden">
            <img
              src={d.image}
              alt={d.title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,130,216,0.15) 0%, rgba(10,130,216,0.0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.85) 100%)",
              }}
            />
            <div className="dark:hidden absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent" />
            <div className="hidden dark:block absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
            <div
              className="absolute top-4 left-4 h-11 w-11 rounded-xl flex items-center justify-center text-white shadow-md"
              style={{ background: "linear-gradient(135deg,#00B4F0,#0A82D8)" }}
            >
              <Icon className="h-5 w-5" strokeWidth={2} />
            </div>
          </div>
          <div className="flex-1 flex flex-col px-6 pb-6 -mt-2">
            <h3 className="font-display font-semibold text-[1.45rem] tracking-tight">
              {d.title}
            </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">{d.tagline}</p>
            <div className="mt-auto flex items-center justify-between pt-5">
              <div className="h-px w-10 bg-gradient-to-r from-[#00B4F0] to-[#0A82D8]" />
              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                {isTouch ? "Tap for details" : "Hover for details"}
              </span>
            </div>
          </div>
        </div>

        {/* BACK — gradient blue with description + bullets + CTA */}
        <div
          className="flip-face flip-back rounded-3xl overflow-hidden text-white flex flex-col p-6"
          style={{ background: "linear-gradient(145deg, #0A82D8 0%, #00B4F0 100%)" }}
        >
          <div className="flex items-start justify-between">
            <h3 className="font-display font-semibold text-[1.45rem] tracking-tight">
              {d.title}
            </h3>
            <Icon className="h-5 w-5 opacity-85" />
          </div>
          <p className="mt-3 text-[13.5px] leading-relaxed text-white/92">
            {d.desc}
          </p>
          <ul className="mt-4 space-y-1.5 text-[12.5px]">
            {d.points.map((p) => (
              <li key={p} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-white/85" />
                <span className="text-white/92">{p}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleAsk}
            className="mt-auto inline-flex items-center justify-center gap-1.5 self-start text-xs font-semibold px-4 py-2 rounded-full bg-white text-[#0A82D8] hover:bg-white/95 transition"
          >
            Ask about {d.title}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const DomainsGrid = () => {
  return (
    <section
      id="domains"
      data-testid="domains-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14"
        >
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#0A82D8]">
            What we do
          </span>
          <h2 className="mt-3 font-display font-semibold text-4xl md:text-5xl lg:text-[3.5rem] tracking-tighter leading-[1.05]">
            Eight practice areas. <br className="hidden sm:block" />
            <span className="text-gradient-cyan">One team you can call.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-xl">
            Hover over each card to see how we help. Most of our clients work with us across two or three of these over time.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {domains.map((d, i) => (
            <DomainCard key={d.id} d={d} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DomainsGrid;
